import type { GameTrackingInfo } from '@/types/games';
import { isRectIntersectingDraggableVisibleArea } from '@/utils/tracking/draggable-clickable-bounds';

const DEFAULT_GRID_SELECTOR = '.grid-zone-layout';

const escapeAttrSelectorValue = (value: string): string =>
    typeof CSS !== 'undefined' && typeof CSS.escape === 'function' ? CSS.escape(value) : value;

const findGameTileInGrid = (grid: Element, gameUid: string): HTMLElement | null => {
    const safe = escapeAttrSelectorValue(gameUid);
    const byGameUid = grid.querySelector(`[data-gameuid="${safe}"]`);
    if (byGameUid instanceof HTMLElement) {
        return byGameUid;
    }
    const byTrackingUid = grid.querySelector(`[data-t-gameuid="${safe}"]`);
    return byTrackingUid instanceof HTMLElement ? byTrackingUid : null;
};

/** Horizontal carousel strip (`use-game-zone-impression` scroll target). */
export const getScrollableFromZoneContainer = (container: HTMLElement): HTMLElement | null => {
    const el = container.querySelector('.draggable-wrapper');
    return el instanceof HTMLElement ? el : null;
};

/**
 * Element used for Snowplow `on_screen`: prefer the thumbnail (`.game__image-container`), not the
 * full tile including labels, so row layouts stay consistent.
 */
export const getGameZoneTileVisibilityElement = (tile: HTMLElement): HTMLElement =>
    (tile.querySelector('.game__image-container') as HTMLElement | null) ??
    (tile.querySelector('a.game__url') as HTMLElement | null) ??
    tile;

/**
 * Thumbnail rect for `on_screen`; falls back to the tile root if the image box has no layout yet.
 */
export const getGameZoneTileBoundingRectForOnScreen = (tile: HTMLElement): DOMRectReadOnly => {
    const visibilityEl = getGameZoneTileVisibilityElement(tile);
    const rect = visibilityEl.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
        return tile.getBoundingClientRect();
    }
    return rect;
};

const VIEWPORT_EDGE_TOLERANCE_PX = 1;

const rectIntersectsVisibleViewport = (r: DOMRectReadOnly): boolean => {
    if (typeof window === 'undefined') {
        return false;
    }
    if (r.width <= 0 || r.height <= 0) {
        return false;
    }
    const t = VIEWPORT_EDGE_TOLERANCE_PX;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    return r.right > -t && r.left < vw + t && r.bottom > -t && r.top < vh + t;
};

/**
 * Snowplow `on_screen`: for carousel zones the tile must lie inside the draggable visible area
 * (between prev/next arrows and within the grid's vertical bounds); for non-carousel zones
 * (static grids) a simple viewport intersection check is enough.
 */
const isGameTileOnScreen = (tile: HTMLElement, container: HTMLElement): boolean => {
    const r = getGameZoneTileBoundingRectForOnScreen(tile);
    if (r.width <= 0 || r.height <= 0) {
        return false;
    }

    const scrollable = getScrollableFromZoneContainer(container);
    if (!scrollable) {
        return rectIntersectsVisibleViewport(r);
    }
    return isRectIntersectingDraggableVisibleArea(r, scrollable);
};

/**
 * Pre-builds a uid → HTMLElement map so scroll handlers can skip per-game `querySelector`.
 * Rebuild when `items` or the container change; reuse across scroll events.
 */
export const buildGameTileMap = (
    container: HTMLElement | null,
    items: Pick<GameTrackingInfo, 'uid'>[],
    gridSelector = DEFAULT_GRID_SELECTOR
): Map<string, HTMLElement> => {
    const map = new Map<string, HTMLElement>();
    if (!container || items.length === 0) return map;
    const grid = container.querySelector(gridSelector);
    if (!grid) return map;
    for (const item of items) {
        const tile = findGameTileInGrid(grid, item.uid);
        if (tile) map.set(item.uid, tile);
    }
    return map;
};

/**
 * Per-game `on_screen` for Snowplow `game_zone_impression` / `game_zone_scroll` contexts.
 * When a `tileMap` is provided (from {@link buildGameTileMap}), tile lookups are O(1) map gets
 * instead of per-game `querySelector` calls — use this for repeated calls (scroll events).
 */
export const getOnScreenFlagsForZoneGames = (
    container: HTMLElement | null,
    items: Pick<GameTrackingInfo, 'uid'>[],
    gridSelector = DEFAULT_GRID_SELECTOR,
    tileMap?: Map<string, HTMLElement>
): boolean[] => {
    if (!container || items.length === 0) {
        return items.map(() => false);
    }

    if (tileMap && tileMap.size > 0) {
        return items.map((game) => {
            const tile = tileMap.get(game.uid);
            return tile ? isGameTileOnScreen(tile, container) : false;
        });
    }

    const grid = container.querySelector(gridSelector);
    if (!grid) {
        return items.map(() => false);
    }
    return items.map((game) => {
        const tile = findGameTileInGrid(grid, game.uid);
        return tile ? isGameTileOnScreen(tile, container) : false;
    });
};
